import type {
  DatasetInstanceConfig,
  ModelVersionSelections,
} from '../data-builder.store';
import type {
  AggregationColumnItem,
  DeliverableColumnConfig,
} from '../datasets/deliverable-column-config';
import { hasCompanyUnitOfAnalysisScope } from './step2-column/company-unit-of-analysis-scope';
import {
  buildConditionContext,
  collectRequiredKeysFromAggregationItems,
  filterAggregationItemsByModels,
  filterBreakdownsByModels,
  filterUnitOptionsByModels,
  isAggregationColumnsVisible,
} from './step2-column/filter-config-by-models';
import { getDisabledAggregationKeys } from './step2-column/get-disabled-aggregation-keys';

const AGGREGATION_BREAKDOWN_ID = 'aggregation';

export const STEP2_VALIDATION_SECTION_UNIT_OF_ANALYSIS = 'unit-of-analysis';
export const STEP2_VALIDATION_SECTION_AGGREGATION = 'aggregation';
export const STEP2_VALIDATION_SECTION_FILES = 'files';

export type Step2ValidationMessage = {
  section:
    | typeof STEP2_VALIDATION_SECTION_UNIT_OF_ANALYSIS
    | typeof STEP2_VALIDATION_SECTION_AGGREGATION
    | typeof STEP2_VALIDATION_SECTION_FILES
    | string;
  text: string;
};

export type Step2InstanceValidationIssue = {
  instanceId: string;
  instanceLabel: string;
  messages: Step2ValidationMessage[];
};

function labelForColumnKey(
  items: AggregationColumnItem[],
  key: string
): string {
  for (const item of items) {
    if (item.type === 'column' && item.option.key === key) {
      return item.option.label;
    }
    if (item.type === 'group') {
      const found = item.options.find((o) => o.key === key);
      if (found !== undefined) {
        return found.label;
      }
    }
  }
  return key;
}

export function buildStep2ValidationResult(
  instances: { instanceId: string; datasetKey: string }[],
  configByInstance: Record<string, DatasetInstanceConfig>,
  deliverablesConfig: Record<string, DeliverableColumnConfig>,
  modelVersions: ModelVersionSelections,
  instanceLabelById: Record<string, string>
): Step2InstanceValidationIssue[] {
  const issues: Step2InstanceValidationIssue[] = [];

  for (const instance of instances) {
    const deliverableConfig = deliverablesConfig[instance.datasetKey];
    const instanceConfig = configByInstance[instance.instanceId];
    if (instanceConfig === undefined) {
      continue;
    }

    const context = buildConditionContext(
      modelVersions,
      instanceConfig.unitOfAnalysis,
      instanceConfig.settings
    );

    const messages: Step2ValidationMessage[] = [];

    if (deliverableConfig === undefined) {
      issues.push({
        instanceId: instance.instanceId,
        instanceLabel:
          instanceLabelById[instance.instanceId] ?? instance.datasetKey,
        messages: [
          {
            section: STEP2_VALIDATION_SECTION_UNIT_OF_ANALYSIS,
            text: 'Dataset configuration is not available',
          },
        ],
      });
      continue;
    }

    const visibleUoa = filterUnitOptionsByModels(
      deliverableConfig.unitOfAnalysis.options,
      context
    );
    const noUoaMessage = 'A unit of analysis selection is required';
    if (visibleUoa.length > 0) {
      if (instanceConfig.unitOfAnalysis === null) {
        messages.push({
          section: STEP2_VALIDATION_SECTION_UNIT_OF_ANALYSIS,
          text: noUoaMessage,
        });
      } else {
        const selectedOption = visibleUoa.find(
          (o) => o.value === instanceConfig.unitOfAnalysis
        );
        if (selectedOption === undefined) {
          messages.push({
            section: STEP2_VALIDATION_SECTION_UNIT_OF_ANALYSIS,
            text: noUoaMessage,
          });
        } else if (instanceConfig.unitOfAnalysis === 'company') {
          if (!hasCompanyUnitOfAnalysisScope(instanceConfig)) {
            messages.push({
              section: STEP2_VALIDATION_SECTION_UNIT_OF_ANALYSIS,
              text: 'A company selection is required',
            });
          }
        } else if (selectedOption.selectionListKeys.length > 0) {
          if (instanceConfig.unitOfAnalysisSelections.length === 0) {
            messages.push({
              section: STEP2_VALIDATION_SECTION_UNIT_OF_ANALYSIS,
              text: `Selections are required for ${selectedOption.label}`,
            });
          }
        }
      }
    }

    if (
      isAggregationColumnsVisible(deliverableConfig.aggregationColumns, context)
    ) {
      const visibleAggItems = filterAggregationItemsByModels(
        deliverableConfig.aggregationColumns.items,
        context
      );
      const requiredAggKeys =
        collectRequiredKeysFromAggregationItems(visibleAggItems);
      const disabledAggKeys = getDisabledAggregationKeys(
        instanceConfig.unitOfAnalysis,
        visibleAggItems
      );
      const selectedAgg =
        instanceConfig.columnKeysByBreakdown[AGGREGATION_BREAKDOWN_ID] ?? [];
      for (const key of requiredAggKeys) {
        if (disabledAggKeys.has(key)) {
          continue;
        }
        if (!selectedAgg.includes(key)) {
          messages.push({
            section: STEP2_VALIDATION_SECTION_AGGREGATION,
            text: `${labelForColumnKey(visibleAggItems, key)} column is required`,
          });
        }
      }
    }

    const visibleBreakdowns = filterBreakdownsByModels(
      deliverableConfig.breakdowns,
      context
    );
    const includedBreakdownIds = new Set(instanceConfig.includedBreakdownIds);
    const includedVisibleBreakdowns = visibleBreakdowns.filter((breakdown) =>
      includedBreakdownIds.has(breakdown.id)
    );

    if (
      visibleBreakdowns.length > 0 &&
      includedVisibleBreakdowns.length === 0
    ) {
      messages.push({
        section: STEP2_VALIDATION_SECTION_FILES,
        text: 'At least one component is required',
      });
    }

    for (const breakdown of includedVisibleBreakdowns) {
      const requiredBdKeys = collectRequiredKeysFromAggregationItems(
        breakdown.columnItems
      );
      const disabledBdKeys = getDisabledAggregationKeys(
        instanceConfig.unitOfAnalysis,
        breakdown.columnItems
      );
      const selectedBd =
        instanceConfig.columnKeysByBreakdown[breakdown.id] ?? [];
      for (const key of requiredBdKeys) {
        if (disabledBdKeys.has(key)) {
          continue;
        }
        if (!selectedBd.includes(key)) {
          messages.push({
            section: breakdown.id,
            text: `${labelForColumnKey(
              breakdown.columnItems,
              key
            )} column is required`,
          });
        }
      }
    }

    if (messages.length > 0) {
      issues.push({
        instanceId: instance.instanceId,
        instanceLabel:
          instanceLabelById[instance.instanceId] ?? instance.datasetKey,
        messages,
      });
    }
  }

  return issues;
}

export function getFirstValidationScrollTarget(
  instances: { instanceId: string; datasetKey: string }[],
  issues: Step2InstanceValidationIssue[]
): { instanceId: string; section: string } | null {
  const issueById = new Map(issues.map((issue) => [issue.instanceId, issue]));
  for (const instance of instances) {
    const issue = issueById.get(instance.instanceId);
    if (issue === undefined || issue.messages.length === 0) {
      continue;
    }
    const firstMessage = issue.messages[0];
    if (firstMessage === undefined) {
      continue;
    }
    return {
      instanceId: issue.instanceId,
      section: firstMessage.section,
    };
  }
  return null;
}

export function step2MessagesToSectionErrorText(
  messages: Step2ValidationMessage[]
): Record<string, string> {
  const bySection: Record<string, string[]> = {};
  for (const message of messages) {
    const list = bySection[message.section] ?? [];
    list.push(message.text);
    bySection[message.section] = list;
  }
  return Object.fromEntries(
    Object.entries(bySection).map(([section, texts]) => [
      section,
      texts.join('; '),
    ])
  );
}
